import { HttpClient, HttpParams } from '@angular/common/http';
import {tap} from 'rxjs/operators';

export interface DjangoPage<T> {
  results: T[];
  count: number;
  next?: string;
  previous?: string;
  more?: boolean;
}

// DRF builds absolute `next` links from the proxied request, which behind the TLS-terminating
// proxy come back as http:// and get blocked as mixed content. Only the path+query matters here,
// so follow them relative to the page we are already on.
export function sameOriginUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.href);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

export class DjangoPaginator<T> {
  url: string;
  next?: string;
  more?: boolean;
  httpClient: HttpClient;
  data: T[] = [];
  count = 0;
  loading = false;

  constructor(url, httpClient, options = {}) {
    this.url = url;
    this.httpClient = httpClient;
  }

  createHttpParams(params: {}): HttpParams {
    let httpParams: HttpParams = new HttpParams();
    Object.keys(params).forEach(param => {
      if (Array.isArray(params[param])) {
        params[param].forEach(val => {
          httpParams = httpParams.append(param, val);
        });
      } else if (params[param] || params[param] === 0) {
        httpParams = httpParams.set(param, params[param]);
      }
    });

    return httpParams;
  }

  loadFirst(params = {}) {
    this.loading = true;
    this.httpClient
      .get<DjangoPage<T>>(this.url, {
        params: this.createHttpParams(params),
      })
      .subscribe({
        next: page => {
          this.next = page.next;
          this.data = page.results;
          this.count = page.count;
          this.more = page.more;
          this.loading = false;
        },
        error: () => (this.loading = false),
      });
  }

  loadFirstAsync(params = {}) {
    // used when you need to modify response objects
    return this.httpClient
      .get<DjangoPage<T>>(this.url, {
        params: this.createHttpParams(params),
      })
      .pipe(
        tap(page => {
          this.next = page.next;
          this.data = page.results;
          this.count = page.count;
          this.more = page.more;
        }),
      );
  }

  nextPage() {
    if (!this.next || this.loading) {
      return;
    }
    this.loading = true;
    this.httpClient.get<DjangoPage<T>>(sameOriginUrl(this.next)).subscribe({
      next: page => {
        this.next = page.next;
        this.data = [...this.data, ...page.results];
        this.count = page.count;
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }
}
